import type { Glyph } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { domeLight, fromSegment, type HeadPicture, mask, type SetFlair, toneOf } from './mythic';

/**
 * The Chronomancer set (ADR-0160 amended, mythic): a clockwork time-mage; a clock for a head, a gear waistcoat, and the
 * Tick-Tock gun.
 *
 * The head is a brass clock (a mask over the whole head, big enough to hide the face under it): a thick copper case
 * turned a little toward where it looks (its side shows at the left), a rounded brass bezel with a glint, an ivory dial
 * with its twelve marks, two eyes of the team's light set in it and a short hour hand standing at twelve between them; a
 * crown of three brass gears behind it. Its flair is the clock going: a long minute hand ticking round the dial (a glyph,
 * a step every half second, its tip in the team's colour), the big crown gear's teeth turning (a glyph behind the head:
 * the head draws only its wheel), the eyes blinking now and then and glowing, gold motes of time-sand falling round it.
 * The waistcoat is copper under a dark frock coat, brass buttons, the team's cravat puffed out under the dial, a brass
 * gear on each shoulder with a fringe in the team's colour; a pendulum swings at its hip and steam puffs from its
 * collar's vent every couple of seconds (glyph frames: a puff swelling, rising, torn to wisps).
 *
 * Rubric (`assetc mythic` at 5×, 8× and 10× and 1:1 on grass, lane and night, both teams, idle, run and dead, the strip
 * 0.1 s apart; compared with Inferno's): clock S5 R5 P4 L4 C4 A4 F4, waistcoat S3 R4 P4 L4 C4 A4 F4. The dial is the
 * signature: a cream disc in a gold ring under a crown of gears reads as a clock on every ground at 1:1, its minute hand
 * ticking while it stands still. Still weak: the waistcoat hardly shows (the dial, which must cover the face, and the
 * gun hide it; its epaulette, fringe and pendulum carry it); in a still (a portrait, a tile) the big crown gear is a
 * toothless wheel and the dial has only its hour hand; the small gears' teeth step unevenly at 5×; the minute hand turns
 * backwards facing left (flair is mirrored). Flair: ~230 draws on average, 312 at most.
 */

// --- Gears -----------------------------------------------------------------------------------------------------------

/**
 * A gear: its middle, the reach of its teeth and of its rim (radii), its teeth, each tooth's width (px) and how far it
 * has turned (a share of a tooth).
 */
type Gear = readonly [cu: number, cv: number, out: number, root: number, teeth: number, width: number, turn: number];
/** A metal's tones: shade, body, lit edge. */
type Metal = readonly [shade: PaletteKey, body: PaletteKey, lit: PaletteKey];
const BRASS: Metal = ['U', 'Y', 'y'];

/** Whether (u, v) is on `g`: inside its rim, or on a square tooth. */
function onGear(u: number, v: number, [cu, cv, out, root, teeth, width, turn]: Gear): boolean {
  const [du, dv] = [u - cu, v - cv];
  const r = Math.hypot(du, dv);
  if (r <= root) return true;
  if (r > out) return false;
  // The nearest tooth's way out, and how far across it (u, v) is.
  const k = Math.round((Math.atan2(dv, du) / (Math.PI * 2)) * teeth - turn) + turn;
  const a = (k / teeth) * Math.PI * 2;
  return Math.abs(-du * Math.sin(a) + dv * Math.cos(a)) < width / 2;
}

/**
 * The colour of `g` at (u, v) in `metal`, lit from (lx, ly), or null off it: its rim and teeth lit toward the light and
 * shaded away from it, a recessed web inside the rim, a boss round a dark axle.
 */
function gearAt(u: number, v: number, g: Gear, metal: Metal, lx: number, ly: number): PaletteKey | null {
  if (!onGear(u, v, g)) return null;
  const [cu, cv, , root] = g;
  const [du, dv] = [u - cu, v - cv];
  const r = Math.hypot(du, dv);
  if (r < 1.2) return 'N';
  const lit = du * lx + dv * ly > 0;
  if (r < 2.4) return lit ? metal[2] : metal[1];
  if (r < root - 1.5) return r < 3.3 && !lit ? 'n' : metal[0];
  const [sx, sy] = [Math.sign(lx), Math.sign(ly)];
  if (!onGear(u + sx, v, g) || !onGear(u, v + sy, g)) return metal[2];
  if (!onGear(u - sx, v, g) || !onGear(u, v - sy, g)) return metal[0];
  return metal[1];
}

// --- The clock -------------------------------------------------------------------------------------------------------

/** The dial's middle (u, v): a pixel's corner, so the hands turn about it evenly. */
const DIAL = [3, 2] as const;
/** The bezel's outer edge and its inner one (the dial's): big enough to hide the face under it. */
const R_OUT = 18;
const R_IN = 14.6;
/** The case's back from its front: back and a little up, so its side shows at the left (it looks right). */
const BACK = [-5, -1.2] as const;
/** The eyes: [u, v, radius], the near one and the far one. */
const EYES: readonly (readonly [number, number, number])[] = [
  [DIAL[0] - 5.8, DIAL[1] - 3.8, 3.1],
  [DIAL[0] + 6, DIAL[1] - 3.8, 2.9],
];
/**
 * The crown of gears behind the case: a big one in the middle (only its wheel here: its teeth are its flair, turning,
 * `TEETH`) and a small one either side.
 */
const WHEEL: Gear = [2, -18, 5.6, 5.6, 8, 2.4, 0];
const CROWN: readonly Gear[] = [WHEEL, [-13.5, -13, 5.8, 4, 6, 2.2, 0.5], [18, -12.5, 5.4, 3.7, 6, 2.2, 0]];

/** The bezel's brass by the light on it: a rounded ring. */
const BEZEL = [
  ['y', 0.62],
  ['Y', 0.05],
  ['U', -0.55],
] as const;
/** The case's copper side, turned toward the light or away. */
const CASE = [
  ['U', 0.55],
  ['u', -0.1],
] as const;
/** The ivory dial under its glass, dimmer turned away from the light. */
const IVORY = [['e', -0.3]] as const;

const clock: HeadPicture = (u, v, lx, ly) => {
  const [du, dv] = [u - DIAL[0], v - DIAL[1]];
  const r = Math.hypot(du, dv);
  // How much the way out from the dial's middle faces the light.
  const facing = r > 0 ? (du * lx + dv * ly) / r : 0;
  if (r <= R_IN) {
    // The eyes: a dark socket, the team's light, a glint.
    for (const [eu, ev, er] of EYES) {
      const e = Math.hypot(u - eu, v - ev);
      if (e < er) {
        if (Math.hypot(u - eu - lx * 0.9, v - ev - ly * 0.9) < 0.75) return 'w';
        return e < er - 1.7 ? 'c' : e < er - 0.8 ? 'b' : 'k';
      }
    }
    // The hour hand at twelve, and its boss.
    if (r < 1.6) return du * lx + dv * ly > 0.3 ? 'y' : 'Y';
    if (fromSegment(u, v, DIAL[0], DIAL[1], DIAL[0], DIAL[1] - 7.4) < 1.2 - Math.max(0, -dv - 5.4) * 0.4) return 'k';
    // Twelve marks: long at the quarters.
    for (let k = 0; k < 12; k++) {
      const a = (k * Math.PI) / 6;
      const [x, y] = [Math.sin(a), -Math.cos(a)];
      const long = k % 3 === 0;
      const [r0, r1] = long ? [R_IN - 3.8, R_IN - 1.5] : [R_IN - 2.6, R_IN - 1.7];
      const d = fromSegment(du, dv, x * r0, y * r0, x * r1, y * r1);
      if (d < (long ? 0.85 : 0.62)) return long ? 'N' : 'n';
    }
    // The bezel's shadow on the dial's lit edge.
    if (r > R_IN - 0.9) return facing > 0.2 ? 't' : 'e';
    return toneOf(domeLight(u, v, [DIAL[0], DIAL[1], R_IN, R_IN], lx, ly), IVORY, 't');
  }
  if (r <= R_OUT) {
    // A dark seam where the bezel meets the dial, then the brass ring rounded over.
    if (r < R_IN + 0.7) return 'n';
    const t = (r - R_IN) / (R_OUT - R_IN);
    // A glint where it faces the light most.
    if (facing > 0.97 && t > 0.45 && t < 0.85) return 'w';
    return toneOf(facing * (t * 2 - 1) * 0.9 + 0.12, BEZEL, 'n');
  }
  // The case's side, knurled.
  const [bu, bv] = [DIAL[0] + BACK[0], DIAL[1] + BACK[1]];
  if (fromSegment(u, v, bu, bv, DIAL[0], DIAL[1]) <= R_OUT) {
    const [nu, nv] = [u - bu, v - bv];
    const n = Math.hypot(nu, nv) || 1;
    const l = (nu * lx + nv * ly) / n;
    return toneOf(l, CASE, 'n');
  }
  // The crown: nothing of it this low.
  if (v > -7) return null;
  for (const g of CROWN) {
    const tone = gearAt(u, v, g, BRASS, lx, ly);
    if (tone) return tone;
  }
  return null;
};

// --- The gear waistcoat ----------------------------------------------------------------------------------------------

/**
 * The waistcoat standing (the frame's x 6-55, y 39-55): a dark frock coat (lit along its top-left) open over a copper
 * waistcoat with brass buttons, the shirt's white collar under the dial and the team's cravat puffed out under it; a
 * brass gear on each shoulder for an epaulette, a fringe in the team's colour hanging off it.
 */
function waistcoat(): Part {
  const [x0, y0] = [6, 39];
  const c = new PixelCanvas(50, 17);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  // The waistcoat: copper, lit along its top-left, dark at its far side and hem; a brass button down the front.
  for (let y = 44; y <= 55; y++)
    for (let x = 18; x <= 42; x++) at(x, y, x <= 19 || y === 44 ? 'U' : x >= 40 || y >= 54 ? 'n' : 'u');
  for (const y of [49, 52, 55]) c.stamp('yY\nUn', 31 - x0, y - y0);
  // The coat: shoulders and sleeves, dark, lit along their tops and backs, cuffed in brass; its lapels open.
  for (const [xa, xb, shade] of [
    [7, 22, false],
    [40, 53, true],
  ] as const)
    for (let y = 44; y <= 55; y++)
      for (let x: number = xa; x <= xb; x++) {
        if (y === 44 && (x === xa || x === xb)) continue;
        const lapel = shade ? x - xa <= 1 + (y - 44) * 0.2 : xb - x <= 1 + (y - 44) * 0.2;
        const lit = !shade && (x <= xa + 1 || y === 44);
        at(x, y, y >= 54 ? (shade ? 'U' : 'Y') : lapel ? 'n' : lit ? 'n' : x >= xb - 1 || shade ? 'k' : 'N');
      }
  // The shirt's collar either side of the chin, and the cravat puffed out under it, in the team's colour.
  c.stamp('wwwe\n.wee', 21 - x0, 43 - y0);
  c.stamp('ewww\neet.', 39 - x0, 43 - y0);
  c.stamp(
    `
    ..cccb..
    .cbbbbB.
    cbbccbbB
    .bbbbBB.
    ..bBBB..
    ..cbB...
  `,
    24 - x0,
    44 - y0,
  );
  // A gear on each shoulder, its fringe hanging under it.
  for (const [g, fx0, fx1] of [
    [[13, 46, 6.6, 4.8, 8, 2.3, 0.5], 8, 18],
    [[47.5, 45.5, 6.2, 4.5, 8, 2.3, 0], 43, 52],
  ] as const) {
    for (let x = fx0; x <= fx1; x++)
      for (let y = 50; y <= 54 - ((x * 3) % 2); y++) at(x, y, y === 50 ? 'B' : x % 2 ? 'b' : 'c');
    for (let y = 39; y <= 55; y++)
      for (let x = 6; x <= 55; x++) {
        const tone = gearAt(x + 0.5, y + 0.5, g, BRASS, -1, -1);
        if (tone) at(x, y, tone);
      }
  }
  return [c, x0, y0];
}

/** The waistcoat lying (right of the legs, under the head): the hem at the waist (left), the cravat at the throat. */
const DEAD_WAISTCOAT = grid(`
  ...UYYyU
  ..UYYYYy
  NNnNNNNN
  nuuuuUew
  nuuuuuec
  nuyuuubb
  nuUuuubB
  nuuuuubb
  nuyuuueb
  nuUuuuee
  nuuuuuuu
  nnuuuuuu
  NNnNNNNN
  NNNNNNNN
  NkNNNNkk
  ..UYYYYU
  ...UYYU.
  ....NN..
`);

/** Copper boots under the team's trousers. */
const GEAR_WAISTCOAT = (): Outfit => ({ torso: waistcoat(), dead: [DEAD_WAISTCOAT, 24, 51], legs: { n: 'u', N: 'N' } });

// --- Clockwork on every other gun ------------------------------------------------------------------------------------

/**
 * The clockwork finish (the default gun is its own, `pollinator_chronomancer`): steel turned to dark bronze glinting
 * gold, wood to copper, brass brightened.
 */
const CLOCKWORK = {
  '1': 'k',
  '2': 'N',
  '3': 'n',
  '4': 'U',
  '5': 'Y',
  N: 'N',
  n: 'u',
  m: 'U',
  t: 'o',
  e: 'Y',
  U: 'Y',
  Y: 'y',
  y: 'e',
} as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const GOLD = [0xfee761, 0xfeae34, 0xd77643] as const;
const STEAM = [WHITE, 0xc0cbdc, 0x8b9bb4] as const;

/** A hand's frames: 24 × 24 px, turning about their middle (a pixel's corner), one a twelfth of a turn on. */
const SIDE = 24;
function hand(len: number, half: number, tip: number): string[] {
  return Array.from({ length: 12 }, (_, k) => {
    const a = (k * Math.PI) / 6;
    const [x1, y1] = [Math.sin(a) * len, -Math.cos(a) * len];
    const rows: string[] = [];
    for (let y = 0; y < SIDE; y++) {
      let row = '';
      for (let x = 0; x < SIDE; x++) {
        const [px, py] = [x + 0.5 - SIDE / 2, y + 0.5 - SIDE / 2];
        const r = Math.hypot(px, py);
        // Its boss over the middle, the hand from a short tail behind it to its tip.
        if (r < 1.2) row += px + py < 0 ? 'y' : 'Y';
        else {
          const along = (px * x1 + py * y1) / len;
          const d = fromSegment(px, py, -x1 * 0.18, -y1 * 0.18, x1, y1);
          const w = along > len - tip ? half * 0.8 : half;
          row += d < w ? (along > len - tip ? 't' : 'k') : '.';
        }
      }
      rows.push(row);
    }
    return rows.join('\n');
  });
}

/** The minute hand: a step every half second, round the dial in six seconds. */
const MINUTE: Glyph = {
  kind: 'glyph',
  // Its middle on the dial's (the head's middle is the frame's [30, 28]).
  at: { on: 'head', x: 30 + DIAL[0] - SIDE / 2, y: 28 + DIAL[1] - SIDE / 2 },
  frames: hand(11, 0.9, 3),
  tones: { k: 0x181425, t: 'team', y: 0xfee761, Y: 0xfeae34 },
  hold: [0.5, 0.5],
  order: 'cycle',
  layer: 'front',
};

/**
 * The big crown gear's teeth, turning (a quarter of a tooth a frame): behind the body, so its wheel (drawn in the head,
 * with the body's rim round it) covers their roots; each tooth lit on its side toward the light, rimmed dark. Only what
 * shows: none of it under the head or its rim.
 */
function teeth(): string[] {
  const [side, n, half, tip] = [28, 8, 1.6, 10.6];
  const [cu, cv] = WHEEL;
  // What the head covers (with its 2-px rim) over the glyph's square, once.
  const head = (x: number, y: number) =>
    clock(cu + x + 0.5 - side / 2, cv + y + 0.5 - side / 2, -Math.SQRT1_2, -Math.SQRT1_2, false) !== null;
  const solid = Array.from({ length: side + 4 }, (_, y) =>
    Array.from({ length: side + 4 }, (_, x) => head(x - 2, y - 2)),
  );
  const hidden = (x: number, y: number) => {
    for (let dy = -2; dy <= 2; dy++)
      for (let dx = -2; dx <= 2; dx++)
        if (Math.abs(dx) + Math.abs(dy) <= 2 && solid[y + dy + 2]?.[x + dx + 2]) return true;
    return false;
  };
  return Array.from({ length: 4 }, (_, k) => {
    const on = (x: number, y: number): string | null => {
      const [px, py] = [x + 0.5 - side / 2, y + 0.5 - side / 2];
      const r = Math.hypot(px, py);
      if (r > tip || r < 5) return null;
      const j = Math.round((Math.atan2(py, px) / (Math.PI * 2)) * n - k / 4) + k / 4;
      const a = (j / n) * Math.PI * 2;
      const across = -px * Math.sin(a) + py * Math.cos(a);
      if (Math.abs(across) >= half) return null;
      // Its side's way out, toward the light (up and left) or away.
      const lit = Math.sign(across) * (Math.sin(a) - Math.cos(a)) * Math.SQRT1_2;
      if (r > tip - 1) return Math.cos(a) + Math.sin(a) < 0 ? 'y' : 'Y';
      return lit > 0.35 ? 'y' : lit < -0.35 ? 'U' : 'Y';
    };
    const tooth = Array.from({ length: side + 4 }, (_, y) =>
      Array.from({ length: side + 4 }, (_, x) => on(x - 2, y - 2)),
    );
    const rows: string[] = [];
    for (let y = 0; y < side; y++) {
      let row = '';
      for (let x = 0; x < side; x++) {
        if (hidden(x, y)) {
          row += '.';
          continue;
        }
        // A tooth, or its rim: 2 px round it.
        let px = tooth[y + 2]?.[x + 2] ?? null;
        for (let dy = -2; dy <= 2 && !px; dy++)
          for (let dx = -2; dx <= 2 && !px; dx++)
            if (Math.abs(dx) + Math.abs(dy) <= 2 && tooth[y + dy + 2]?.[x + dx + 2]) px = 'N';
        row += px ?? '.';
      }
      rows.push(row);
    }
    return rows.join('\n');
  });
}

const TEETH: Glyph = {
  kind: 'glyph',
  // Its middle on the wheel's (the head's middle is the frame's [30, 28]).
  at: { on: 'head', x: 30 + WHEEL[0] - 14, y: 28 + WHEEL[1] - 14 },
  frames: teeth(),
  tones: { y: 0xfee761, Y: 0xfeae34, U: 0xd77643, N: 0x3e2731 },
  hold: [0.2, 0.2],
  order: 'cycle',
  layer: 'back',
};

/** The eyes shut a moment now and then: the dial over them, a dark lid's line across each. */
const BLINK: Glyph = {
  kind: 'glyph',
  at: { on: 'head', x: 24, y: 22 },
  frames: [
    Array.from({ length: 9 }, (_, y) =>
      Array.from({ length: 19 }, (_, x) => {
        const [u, v] = [x + 24.5 - 30, y + 22.5 - 28];
        for (const [eu, ev, er] of EYES) {
          if (Math.hypot(u - eu, v - ev) >= er) continue;
          return Math.abs(v - ev - 0.6 + ((u - eu) / er) ** 2 * 0.9) < 0.6 ? 'k' : 'e';
        }
        return '.';
      }).join(''),
    ).join('\n'),
  ],
  tones: { e: 0xead4aa, k: 0x181425 },
  hold: [2.6, 4.4],
  shown: 0.045,
  layer: 'front',
};

/** Motes of time-sand, trickling down. */
const SAND = {
  rate: 5,
  v: [-3, 3, 4, 12],
  carry: 0.6,
  g: 10,
  drag: 0.7,
  wobble: [1, 1.2],
  life: [1, 1.7],
  size: [1, 1],
  tones: GOLD,
  add: true,
  glow: 5,
} as const;

/**
 * A puff of steam out of a vent, as a glyph's frames (11 × 15 px, the vent at the bottom middle): swelling as it rises,
 * lit on its top-left, torn to wisps, then nothing for `rest` frames.
 */
function puff(rest: number): string[] {
  const steps: readonly (readonly (readonly [number, number, number])[])[] = [
    [[5.5, 13.5, 1.5]],
    [
      [5.5, 11.4, 2.4],
      [5, 13.8, 1.2],
    ],
    [
      [5.8, 8.6, 3.2],
      [5, 12, 1.8],
    ],
    [
      [5.2, 5.6, 3.7],
      [6.4, 9.6, 2.1],
    ],
    [[4.8, 3.4, 3.9]],
  ];
  const frames = steps.map((circles, f) =>
    Array.from({ length: 15 }, (_, y) =>
      Array.from({ length: 11 }, (_, x) => {
        for (const [i, [cx, cy, r]] of circles.entries()) {
          const [dx, dy] = [x + 0.5 - cx, y + 0.5 - cy];
          const d = Math.hypot(dx, dy) / r;
          if (d >= 1) continue;
          // Torn as it goes: the big puff's middle gone, then gaps round its rim.
          if (f >= 3 && i === 0 && (d < 0.5 || Math.sin(Math.atan2(dy, dx) * 3 + f) > (f === 4 ? 0 : 0.6))) continue;
          return dx + dy < -r * 0.6 ? 'w' : dx + dy > r * 0.5 ? '4' : '5';
        }
        return '.';
      }).join(''),
    ).join('\n'),
  );
  return [...frames, ...Array.from({ length: rest }, () => '')];
}

/** Steam out of a vent whose foot is at `at` (a puff every few seconds). */
const steam = (on: 'torso' | 'gun', x: number, y: number, rest: number, layer: 'front' | 'gun'): Glyph => ({
  kind: 'glyph',
  at: { on, x: x - 5, y: y - 14 },
  frames: puff(rest),
  tones: { w: WHITE, '5': STEAM[1], '4': STEAM[2] },
  hold: [0.09, 0.09],
  order: 'cycle',
  layer,
});

const FLAIR: SetFlair = {
  head: [
    // A warm light behind the clock, breathing.
    {
      kind: 'pulse',
      at: { on: 'head', x: 30 + DIAL[0], y: 28 + DIAL[1] },
      r: [17, 21],
      alpha: [0.1, 0.2],
      period: 1,
      tone: 0xfeae34,
      layer: 'back',
    },
    TEETH,
    BLINK,
    MINUTE,
    // Its eyes glow in the team's light.
    ...EYES.map(
      ([u, v, r]) =>
        ({
          kind: 'pulse',
          at: { on: 'head', x: 30 + u, y: 28 + v },
          r: [r, r + 1.5],
          alpha: [0.3, 0.55],
          period: 1,
          tone: 'teamLight',
          layer: 'front',
        }) as const,
    ),
    // Time-sand trickling off the clock.
    { kind: 'emit', at: { on: 'head', x: 30 + DIAL[0], y: 30 }, box: [58, 34], ...SAND, layer: 'back' },
  ],
  outfit: [
    // The pendulum at its hip (left of the gun, so it shows): a brass rod and bob, swinging on its own beat.
    {
      kind: 'chain',
      at: { on: 'torso', x: 18, y: 49 },
      n: 7,
      seg: 2.3,
      dir: Math.PI * 0.53,
      stiff: 30,
      g: 60,
      drag: 0.05,
      sway: [0.38, 0.9, 0],
      w: [1, 1, 6],
      shape: 'dots',
      tones: [0xd77643, 0xd77643, 0xfeae34, 0xfeae34, 0xfee761],
      rim: 0x3e2731,
      lit: 0xffffff,
      layer: 'front',
    },
    // Steam from the collar's vent now and then.
    steam('torso', 9, 42, 17, 'front'),
  ],
  gun: [
    // Steam off its vent, a warm light on its flywheel and a glint on it.
    steam('gun', 41, 7, 11, 'gun'),
    {
      kind: 'pulse',
      at: { on: 'gun', x: 27.5, y: 9.5 },
      r: [4, 6],
      alpha: [0.12, 0.25],
      period: 0.8,
      tone: 0xfeae34,
      layer: 'gun',
    },
    {
      kind: 'twinkle',
      at: { on: 'gun', x: 27, y: 9 },
      box: [12, 10],
      n: 2,
      period: [0.8, 1.4],
      tones: [WHITE, 0xfee761],
      add: true,
      layer: 'gun',
    },
  ],
};

/** The Chronomancer set (ADR-0160 amended). */
export const CHRONOMANCER = drawnLater({
  head: () => mask(clock),
  outfit: GEAR_WAISTCOAT,
  gun: CLOCKWORK,
  flair: FLAIR,
});
